import React, { FC } from 'react';
import { UnderlineTextButton } from '@components/UnderlineTextButton';
import { Divider } from '@mui/material';
import { useModelFilters } from '@store/zustand/model-filters';
import { selectionCoverage, toggleAll } from '@utils/cisCatalogue';
import { ScopeDomain, scopeDomainValueIds } from '@utils/domains';
import FilterPanelBackTitle from '@views/Project/components/ModelFiltersPopover/components/FilterPanelBackTitle';
import FilterPanelLayout from '@views/Project/components/ModelFiltersPopover/components/FilterPanelLayout';
import FilterSubtitle from '@views/Project/components/ModelFiltersPopover/components/FilterSubtitle';
import FilterValueRow from '@views/Project/components/ModelFiltersPopover/components/FilterValueRow';
import { useSetCategory } from '@views/Project/components/ModelFiltersPopover/useSetCategory';

export const SCOPE_SUB_DOMAINS_LABEL = 'Sub-domains';

export const SCOPE_SELECT_ALL_LABEL = 'Select all';

type Props = {
  domain: ScopeDomain;
  counts?: Record<string, number>;
  onBack: () => void;
};

/**
 * One Domain's values, rendered beside the Scope panel as its side panel.
 *
 * Headed by the Domain's name, it lists Select all, a divider, the Domain
 * itself, then a "Sub-domains" group with each Sub-domain in
 * Project-settings order; a Domain without Sub-domains lists its own
 * checkbox alone. The Domain and its Sub-domains are selected independently
 * and each is one value: ticking the Domain keeps entities that reference
 * the Domain itself, never those that reference only its Sub-domains.
 * Select all reaches exactly the Domain and its Sub-domains, reads checked
 * once all are selected and indeterminate for some, and from all unselects
 * them. "Clear all" appears while any of them is selected and removes those
 * alone, leaving the rest of Scope. The Scope type is chosen on the category
 * panel and applies to the whole hierarchy, so there is no selector here.
 * Every write goes through `useSetCategory`. Counts are rendered only when
 * the container supplies them, on every row but Select all; a value missing
 * from the map reads 0.
 */
const FilterScopeDomainPanel: FC<Props> = ({ domain, counts, onBack }) => {
  const { domains: selected } = useModelFilters();
  const setCategory = useSetCategory();

  const ids = scopeDomainValueIds(domain);
  const coverage = selectionCoverage(ids, selected);
  const hasSubDomains = domain.subDomains.length > 0;

  const toggle = (id: string) =>
    setCategory(
      'domains',
      selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id],
    );

  const clear = () =>
    setCategory(
      'domains',
      selected.filter((id) => !ids.includes(id)),
    );

  const countOf = (id: string) => (counts ? counts[id] ?? 0 : undefined);

  return (
    <FilterPanelLayout
      title={<FilterPanelBackTitle title={domain.label} onBack={onBack} />}
      action={
        coverage !== 'none' && (
          <UnderlineTextButton
            $disabled={false}
            onClick={clear}
            sx={{ flexShrink: 0 }}
          >
            Clear all
          </UnderlineTextButton>
        )
      }
    >
      {hasSubDomains && (
        <>
          <FilterValueRow
            label={SCOPE_SELECT_ALL_LABEL}
            checked={coverage === 'all'}
            indeterminate={coverage === 'some'}
            onToggle={() => setCategory('domains', toggleAll(selected, ids))}
          />

          <Divider sx={{ borderColor: 'grey.500' }} />
        </>
      )}

      <FilterValueRow
        label={domain.label}
        checked={selected.includes(domain.id)}
        count={countOf(domain.id)}
        onToggle={() => toggle(domain.id)}
      />

      {hasSubDomains && (
        <>
          <FilterSubtitle>{SCOPE_SUB_DOMAINS_LABEL}</FilterSubtitle>

          {domain.subDomains.map((subDomain) => (
            <FilterValueRow
              key={subDomain.id}
              label={subDomain.label}
              checked={selected.includes(subDomain.id)}
              count={countOf(subDomain.id)}
              onToggle={() => toggle(subDomain.id)}
            />
          ))}
        </>
      )}
    </FilterPanelLayout>
  );
};

export default FilterScopeDomainPanel;
